// Helpers for the local encrypt/decrypt tools (ToolsPage, crypto-spec §4).
// The crypto itself is in @sss/crypto; these only shape names, limits and
// messages around it.

import { CryptoError, MAX_FILE_NAME_BYTES, MAX_MESSAGE_FRAME_BYTES } from '@sss/crypto';

const encoder = new TextEncoder();

/** Bytes of UTF-8. */
const utf8Length = (text: string): number => encoder.encode(text).length;

/** Frame overhead: content type and length (5) plus, for files, the name-length byte. */
const FRAME_OVERHEAD = 5;

/** Whether text of `text` fits in one message frame. */
export const textFits = (text: string): boolean => FRAME_OVERHEAD + utf8Length(text) <= MAX_MESSAGE_FRAME_BYTES;

/** Whether a file of `size` bytes named `name` (after fitFileName) fits in one message frame. */
export const fileFits = (name: string, size: number): boolean => FRAME_OVERHEAD + 1 + utf8Length(name) + size <= MAX_MESSAGE_FRAME_BYTES;

/** The largest file the tool takes, for the hint (a short name assumed). */
export const MAX_FILE_BYTES = MAX_MESSAGE_FRAME_BYTES - FRAME_OVERHEAD - 1 - 64;

/** Shorten a name to MAX_FILE_NAME_BYTES of UTF-8, keeping its extension, without splitting a character. */
export const fitFileName = (name: string): string => {
  const clean = name.normalize('NFC') || 'file';
  if (utf8Length(clean) <= MAX_FILE_NAME_BYTES) return clean;
  const dot = clean.lastIndexOf('.');
  const ext = dot > 0 && clean.length - dot <= 16 ? clean.slice(dot) : '';
  let stem = [...clean.slice(0, clean.length - ext.length)];
  while (stem.length > 0 && utf8Length(stem.join('') + ext) > MAX_FILE_NAME_BYTES) stem = stem.slice(0, -1);
  return stem.join('') + ext;
};

/**
 * A decrypted file name is chosen by whoever sealed the message, so treat it
 * as a suggestion: no path separators, control characters or characters
 * Windows refuses, and no leading dots (hidden files) or trailing dots/spaces.
 */
export const safeFileName = (name: string): string => {
  const cleaned = name
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point
    .replace(/[\u0000-\u001f\u007f/\\:*?"<>|]/g, '_')
    .replace(/^[.\s]+/, '')
    .replace(/[.\s]+$/, '');
  return cleaned.length > 0 ? cleaned : 'decrypted-file';
};

/** The download name for a sealed file: `<name>.sss`. */
export const sealedFileName = (name: string): string => `${safeFileName(name)}.sss`;

/** Typed passwords shorter than this get a warning: the message can be attacked offline. */
export const MIN_STRONG_PASSPHRASE = 12;

export const isShortPassphrase = (passphrase: string): boolean => passphrase.length > 0 && [...passphrase].length < MIN_STRONG_PASSPHRASE;

export type ToolProblem = 'wrongPassword' | 'notAMessage' | 'incomplete' | 'noPassword' | 'tooLarge' | 'failed';

/** Map a crypto failure to a message key under `tools.errors.*`. */
export const toolProblem = (err: unknown): ToolProblem => {
  if (!(err instanceof CryptoError)) return 'failed';
  switch (err.code) {
    case 'decryptionFailed':
      return 'wrongPassword';
    case 'unsupportedVersion':
      return 'notAMessage';
    case 'invalidEncoding':
    case 'malformedPayload':
      return 'incomplete';
    case 'invalidPassphrase':
      return 'noPassword';
    case 'contentTooLarge':
      return 'tooLarge';
    default:
      return 'failed';
  }
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace(/\.0$/, '')} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2).replace(/\.?0+$/, '')} GB`;
};

/** Save bytes as a file through a temporary object URL. */
export const downloadBytes = (bytes: Uint8Array<ArrayBuffer>, fileName: string): void => {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  // Give the browser a moment to start the download before the URL goes.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};
